
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";

interface RelatedCoursesSectionProps {
  creatorId: string;
  currentCourseId: string;
  creatorUsername?: string;
}

export const RelatedCoursesSection: React.FC<RelatedCoursesSectionProps> = ({
  creatorId,
  currentCourseId,
  creatorUsername = ""
}) => {
  // Don't attempt to query if creatorId is undefined
  const enabled = Boolean(creatorId && currentCourseId);
  
  const { data: relatedCourses, isLoading, error } = useQuery({
    queryKey: ['related-courses', creatorId, currentCourseId],
    queryFn: async () => {
      // Add a guard clause to prevent the query from running with invalid parameters
      if (!creatorId || !currentCourseId) {
        return [];
      }
      
      // Fetch published courses by the same creator, excluding the current course
      const { data, error } = await supabase
        .from('courses')
        .select(`
          id,
          title,
          description,
          price,
          slug,
          thumbnail_url
        `)
        .eq('creator_id', creatorId)
        .eq('published', true)
        .neq('id', currentCourseId)
        .limit(3);
      
      if (error) throw error;
      return data || [];
    },
    enabled: enabled, // Only run the query if we have valid parameters
  });
  
  if (!enabled || error || (!isLoading && (!relatedCourses || relatedCourses.length === 0))) {
    return null;
  }
  
  if (isLoading) {
    return (
      <section id="related" className="border-t pt-8">
        <h2 className="text-2xl font-bold mb-6">More Courses by This Creator</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="overflow-hidden bg-muted/20 animate-pulse h-64">
              <div className="w-full h-full" />
            </Card>
          ))}
        </div>
      </section>
    );
  }
  
  return (
    <section id="related" className="border-t pt-8">
      <h2 className="text-2xl font-bold mb-6">More Courses by This Creator</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {relatedCourses.map((course) => (
          <Card key={course.id} className="overflow-hidden hover:shadow-md transition-shadow">
            <Link to={`/${creatorUsername}/${course.slug}`} className="block h-full">
              <AspectRatio ratio={16 / 9}>
                {course.thumbnail_url ? (
                  <img 
                    src={course.thumbnail_url} 
                    alt={course.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <span className="text-muted-foreground">No Preview</span>
                  </div>
                )}
              </AspectRatio>
              
              <CardContent className="p-4">
                <h3 className="font-semibold line-clamp-2 mb-2">{course.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{course.description}</p>
              </CardContent>
              
              <CardFooter className="px-4 pb-4 pt-0">
                <div className="flex items-center justify-between w-full">
                  <Badge variant={course.price ? "default" : "outline"}>
                    {course.price ? `$${course.price}` : 'Free'}
                  </Badge>
                </div>
              </CardFooter>
            </Link>
          </Card>
        ))}
      </div>
    </section>
  );
};
